import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

import { Offer } from 'common/contracts';

interface IUseOfferFormDataProps {
  offerId: string | undefined;
  publisherId: string | undefined;
  getOffer: {
    data?: { result?: Partial<Offer> };
    isLoading: boolean;
    refetch: () => void;
  };
  onDataLoaded?: (result: Partial<Offer>) => void;
}

interface IUseOfferFormDataReturn {
  data: Partial<Offer>;
  setData: Dispatch<SetStateAction<Partial<Offer>>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  chosenSegment: string[];
  setChosenSegment: Dispatch<SetStateAction<string[]>>;
}

/**
 * Manages the shared offer form data state: data, isLoading, chosenSegment,
 * automatic refetch when offerId/publisherId change, segment sync on data change,
 * isLoading management, and an optional onDataLoaded callback for form-specific
 * initialization (products, progressBar, etc.).
 *
 * @example
 * const { data, setData, isLoading, chosenSegment, setChosenSegment } = useOfferFormData({
 *   offerId: bundleId,
 *   publisherId,
 *   getOffer,
 *   onDataLoaded: (result) => {
 *     setCurrentOfferUI(result.offerUi?._id || '');
 *     setProducts(mapApiProductsToLocal(result.productsSequence[0].products));
 *   }
 * });
 */
export const useOfferFormData = ({
  offerId,
  publisherId,
  getOffer,
  onDataLoaded
}: IUseOfferFormDataProps): IUseOfferFormDataReturn => {
  const [data, setData] = useState<Partial<Offer>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [chosenSegment, setChosenSegment] = useState<string[]>([]);

  // Stable reference for the callback
  const onDataLoadedRef = useCallback(
    (result: Partial<Offer>): void => {
      onDataLoaded?.(result);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Refetch when offerId or publisherId changes
  useEffect(() => {
    if (offerId && publisherId) {
      getOffer.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, publisherId]);

  // Initialize data when offer loads
  useEffect(() => {
    if (offerId && !getOffer.isLoading && getOffer.data?.result) {
      setData(getOffer.data.result);
      onDataLoadedRef(getOffer.data.result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, getOffer.data, getOffer.isLoading]);

  // Sync chosenSegment when data.segments changes
  useEffect(() => {
    if (data?.segments) {
      setChosenSegment(data.segments);
    }
  }, [data?.segments]);

  // Set isLoading to false when data is available
  useEffect(() => {
    if (data) {
      setIsLoading(false);
    }
  }, [data]);

  return { data, setData, isLoading, setIsLoading, chosenSegment, setChosenSegment };
};
